import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { supabase } from "../lib/supabase";
import { C, Btn, Card, Stars } from "../components/UI";

const CRITERIA = [
  { key: "nota_salario", label: "Igualdade salarial" },
  { key: "nota_lideranca_feminina", label: "Liderança feminina" },
  { key: "nota_maternidade", label: "Política de maternidade" },
  { key: "nota_ambiente", label: "Ambiente de trabalho" },
  { key: "nota_crescimento", label: "Crescimento de carreira" },
  { key: "nota_flexibilidade", label: "Flexibilidade" },
];

const POSITIVE_CHIPS = ["Flexibilidade", "Diversidade e inclusão", "Salário competitivo", "Liderança feminina", "Auxílio creche", "Home office", "Benefícios", "Cultura de respeito", "Maternidade apoiada", "Crescimento real"];
const NEGATIVE_CHIPS = ["Desigualdade salarial", "Assédio não tratado", "Cultura machista", "Poucas líderes mulheres", "Falta de flexibilidade", "Sem política de maternidade", "Pressão excessiva", "Discriminação velada"];

const STAR_DESC = ["", "Muito ruim — não recomendo", "Ruim — muitos problemas", "Regular — tem prós e contras", "Bom — vale a pena considerar", "Excelente — recomendo muito"];

export default function ReviewForm() {
  const { user, loading: authLoading } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mainStar, setMainStar] = useState(0);
  const [criteria, setCriteria] = useState({});
  const [posChips, setPosChips] = useState([]);
  const [negChips, setNegChips] = useState([]);
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(true);
  const [company, setCompany] = useState("");
  const [cargo, setCargo] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Exige login para avaliar
  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  // Busca empresas ja cadastradas enquanto a usuaria digita
  useEffect(() => {
    const termo = company.trim();
    if (termo.length < 2 || !showSuggestions) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from("empresas")
        .select("id, nome")
        .ilike("nome", `%${termo}%`)
        .limit(5);
      setSuggestions(data || []);
    }, 300);
    return () => clearTimeout(timer);
  }, [company, showSuggestions]);

  const toggleCriteria = (key, val) => setCriteria(c => ({ ...c, [key]: val }));
  const toggleChip = (chip, list, setList) => {
    setList(prev => prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]);
  };

  // Garante que existe uma linha em "mulheres" para esta usuaria
  const getMulherId = async () => {
    const { data: existente } = await supabase
      .from("mulheres")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (existente) return existente.id;

    const { data: nova, error: erroInsert } = await supabase
      .from("mulheres")
      .insert({
        user_id: user.id,
        email: user.email,
        nome: user.user_metadata?.name || user.email,
        status: "pendente",
      })
      .select("id")
      .single();

    if (erroInsert) throw erroInsert;
    return nova.id;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError("");

    try {
      const mulherId = await getMulherId();

      // Busca a empresa pelo nome; cria como pendente se nao existir
      const { data: empresaId, error: erroEmpresa } = await supabase
        .rpc("buscar_ou_criar_empresa", { p_nome: company.trim() });

      if (erroEmpresa) throw erroEmpresa;

      const { error: erroAvaliacao } = await supabase
        .from("avaliacoes")
        .insert({
          empresa_id: empresaId,
          mulher_id: mulherId,
          cargo: cargo.trim() || null,
          nota_geral: mainStar,
          nota_salario: criteria.nota_salario || null,
          nota_lideranca_feminina: criteria.nota_lideranca_feminina || null,
          nota_maternidade: criteria.nota_maternidade || null,
          nota_ambiente: criteria.nota_ambiente || null,
          nota_crescimento: criteria.nota_crescimento || null,
          nota_flexibilidade: criteria.nota_flexibilidade || null,
          comentario: text.trim(),
          pontos_positivos: posChips,
          pontos_negativos: negChips,
          anonima: anonymous,
          status: "pendente",
        });

      if (erroAvaliacao) {
        if (erroAvaliacao.code === "23505") {
          setError("Você já avaliou essa empresa. Cada pessoa pode enviar uma avaliação por empresa.");
        } else {
          setError("Não foi possível enviar sua avaliação. Tente novamente em alguns instantes.");
        }
        setSaving(false);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError("Não foi possível enviar sua avaliação. Tente novamente em alguns instantes.");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setStep(1);
    setMainStar(0);
    setCriteria({});
    setPosChips([]);
    setNegChips([]);
    setText("");
    setCompany("");
    setCargo("");
    setError("");
  };

  const stepProgress = (
    <div style={{ display: "flex", gap: 6, marginBottom: 28, alignItems: "center" }}>
      {[1, 2, 3].map(s => (
        <div key={s} style={{ flex: 1, height: 3, borderRadius: 100, background: step >= s ? C.roseMid : C.border, transition: "background 0.3s" }} />
      ))}
      <span style={{ fontSize: 12, color: C.textSec, marginLeft: 8, whiteSpace: "nowrap" }}>Etapa {step} de 3</span>
    </div>
  );

  if (authLoading) {
    return (
      <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: C.textSec }}>Carregando...</p>
      </div>
    );
  }

  if (submitted) return (
    <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${C.rosePale} 0%, #faf9f7 60%)` }}>
      <Card style={{ maxWidth: 420, width: "100%", padding: "48px 36px", textAlign: "center" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.greenBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, margin: "0 auto 20px" }}>✓</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.green, marginBottom: 10 }}>Avaliação enviada!</h2>
        <p style={{ fontSize: 14, color: C.textSec, lineHeight: 1.7, marginBottom: 24 }}>
          Obrigada! Nossa equipe analisa sua avaliação antes de publicar — normalmente em até 48 horas. Você pode acompanhar em "Minhas avaliações".
        </p>
        <Btn full onClick={() => navigate("/")}>Voltar para o início</Btn>
        <Btn variant="ghost" full onClick={resetForm} style={{ marginTop: 8, fontSize: 13 }}>Avaliar outra empresa</Btn>
      </Card>
    </div>
  );

  return (
    <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${C.rosePale} 0%, #faf9f7 60%)`, padding: 24 }}>
      <Card style={{ width: "100%", maxWidth: 540, padding: 0, overflow: "hidden" }}>
        <div style={{ background: C.roseMid, padding: "24px 32px" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#fff", marginBottom: 4 }}>Avaliar empresa</h2>
          <p style={{ fontSize: 13, color: C.roseBorder }}>Sua experiência ajuda outras mulheres</p>
        </div>

        <div style={{ padding: "28px 32px" }}>
          {stepProgress}

          {/* STEP 1: Empresa + nota geral */}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ position: "relative" }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 8 }}>Nome da empresa *</label>
                <input
                  value={company}
                  onChange={e => { setCompany(e.target.value); setShowSuggestions(true); }}
                  placeholder="Comece a digitar o nome da empresa"
                  style={{ width: "100%", border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 14, boxSizing: "border-box" }}
                />
                {suggestions.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 10, marginTop: 4, zIndex: 10, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                    {suggestions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setCompany(s.nome); setShowSuggestions(false); setSuggestions([]); }}
                        style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", borderBottom: `0.5px solid ${C.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = C.rosePale}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                      >
                        {s.nome}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 12, color: C.textSec, marginTop: 6 }}>
                  Se a empresa ainda não estiver na lista, é só escrever o nome completo.
                </div>
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 10 }}>Avaliação geral *</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  {[1,2,3,4,5].map(v => (
                    <span key={v} onClick={() => setMainStar(v)} style={{ fontSize: 36, color: mainStar >= v ? C.roseMid : "#F4C0D1", cursor: "pointer", transition: "color 0.1s, transform 0.1s", transform: mainStar >= v ? "scale(1.1)" : "scale(1)" }}>★</span>
                  ))}
                </div>
                {mainStar > 0 && <div style={{ fontSize: 13, color: C.textSec, fontStyle: "italic" }}>{STAR_DESC[mainStar]}</div>}
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 8 }}>Cargo que ocupava</label>
                <input
                  value={cargo}
                  onChange={e => setCargo(e.target.value)}
                  placeholder="Ex: Engenheira de Software Plena"
                  style={{ width: "100%", border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 14, boxSizing: "border-box" }}
                />
              </div>

              <Btn full onClick={() => setStep(2)} disabled={company.trim().length < 2 || !mainStar}>Continuar →</Btn>
            </div>
          )}

          {/* STEP 2: Criterios + chips */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 12 }}>Critérios específicos</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {CRITERIA.map(c => (
                    <div key={c.key} style={{ background: C.grayBg, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{c.label}</div>
                      <Stars value={criteria[c.key] || 0} size={18} interactive onChange={v => toggleCriteria(c.key, v)} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 10 }}>Pontos positivos</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {POSITIVE_CHIPS.map(chip => (
                    <span key={chip} onClick={() => toggleChip(chip, posChips, setPosChips)} style={{ border: posChips.includes(chip) ? `1px solid ${C.greenBorder}` : `0.5px solid ${C.border}`, background: posChips.includes(chip) ? C.greenBg : "transparent", color: posChips.includes(chip) ? C.green : C.textSec, borderRadius: 100, padding: "5px 13px", fontSize: 12, cursor: "pointer", transition: "all 0.15s" }}>{chip}</span>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 10 }}>Pontos a melhorar</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {NEGATIVE_CHIPS.map(chip => (
                    <span key={chip} onClick={() => toggleChip(chip, negChips, setNegChips)} style={{ border: negChips.includes(chip) ? "1px solid #F7C1C1" : `0.5px solid ${C.border}`, background: negChips.includes(chip) ? C.redBg : "transparent", color: negChips.includes(chip) ? C.red : C.textSec, borderRadius: 100, padding: "5px 13px", fontSize: 12, cursor: "pointer", transition: "all 0.15s" }}>{chip}</span>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="secondary" onClick={() => setStep(1)}>← Voltar</Btn>
                <Btn full onClick={() => setStep(3)}>Continuar →</Btn>
              </div>
            </div>
          )}

          {/* STEP 3: Texto + privacidade */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSec, display: "block", marginBottom: 8 }}>Conte sua experiência *</label>
                <textarea value={text} onChange={e => setText(e.target.value)} rows={5} placeholder="O que você gostaria que outras mulheres soubessem sobre trabalhar nessa empresa? Seja honesta — você está ajudando alguém a tomar uma decisão importante." style={{ width: "100%", border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", fontSize: 14, resize: "none", lineHeight: 1.6, boxSizing: "border-box" }} />
                <div style={{ fontSize: 11, color: C.textSec, marginTop: 4, textAlign: "right" }}>{text.length} caracteres</div>
              </div>

              <div style={{ background: C.grayBg, borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div onClick={() => setAnonymous(a => !a)} style={{ width: 40, height: 22, background: anonymous ? C.roseMid : C.border, borderRadius: 100, position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 3, left: anonymous ? "auto" : 3, right: anonymous ? 3 : "auto", width: 16, height: 16, background: "#fff", borderRadius: "50%", transition: "all 0.2s" }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Publicar de forma anônima</div>
                  <div style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>Apenas seu cargo será exibido</div>
                </div>
              </div>

              <div style={{ background: C.rosePale, border: `0.5px solid ${C.roseBorder}`, borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#7A0046", lineHeight: 1.6 }}>
                🔒 Sua avaliação passa por moderação antes de ser publicada. Conteúdo ofensivo ou que identifique pessoas é removido.
              </div>

              {error && (
                <div style={{ background: C.redBg, border: "0.5px solid #F7C1C1", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: C.red, lineHeight: 1.6 }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="secondary" onClick={() => setStep(2)} disabled={saving}>← Voltar</Btn>
                <Btn full onClick={handleSubmit} disabled={text.trim().length < 30 || saving}>
                  {saving ? "Enviando..." : "Enviar avaliação"}
                </Btn>
              </div>
              {text.length < 30 && text.length > 0 && <div style={{ fontSize: 12, color: C.textSec, textAlign: "center" }}>Escreva pelo menos 30 caracteres</div>}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
